import {compact} from '@benchling/js-utils';
import {Unit as CoreUnit} from '@benchling/units';
import React from 'react';
import isEqual from 'react-fast-compare';

import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {AccessibleGridCell} from 'constants/SampleConstants';
import {getCompatibilityFilterFromSymbols} from 'inventory/InventoryUnitUtil';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {MeasurementType, NewMeasurementForm} from 'samples/MeasurementForm';
import {
  MinimalSourceContainerSummary,
  getLegacyMeasurementString,
  validateAllTransferQuantities,
} from 'samples/transfers/TransferUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {GenericEvent, NumericInputChangeEvent} from 'util/Types';

const QUANTITY_OPTIONS = {
  TRANSFER_ALL: 'TRANSFER_ALL',
  SPECIFY_QUANTITIES: 'SPECIFY_QUANTITIES',
};

function InsufficientSourceQuantityError(props: {
  invalidWells: Array<MinimalSourceContainerSummary>;
  transferQuantity: LegacyMeasurement;
  isBlocking: boolean;
}): JSX.Element {
  const quantityString = getQuantityDisplayString(false);
  const baseErrorText = `The ${quantityString} you are trying to transfer exceeds what is available in one or more of the source wells.`;
  const mainErrorText = props.isBlocking
    ? baseErrorText
    : `${baseErrorText} The full ${quantityString} will be transferred, and these source wells will have their ${getQuantityDisplayString(
        false,
        true
      )} set to 0.`;
  const invalidWellsList = (
    <ul className="u-padding-left">
      {props.invalidWells.map((well) => (
        <li key={well.id}>{`${well.name} (${getLegacyMeasurementString(well.quantity)})`}</li>
      ))}
    </ul>
  );
  return (
    <div className="u-small-spacing-top">
      <InlineBanner
        bannerType={props.isBlocking ? InlineBannerType.ERROR : InlineBannerType.WARNING}
        text={mainErrorText}
        supportingInfo={invalidWellsList}
        action={{actionType: InlineBannerActionType.ACCORDION}}
      />
    </div>
  );
}

export interface TransferPlatesQuantityPaneState {
  shouldTransferAllQuantities: boolean;
  transferQuantity: LegacyMeasurement;
  hasTransferQuantityError: boolean;
  wellsWithInsufficientQuantity: Array<MinimalSourceContainerSummary>;
  quantityErrorsAreBlocking: boolean;
}

export const DEFAULT_TRANSFER_PLATES_QUANTITY_STATE: TransferPlatesQuantityPaneState = {
  shouldTransferAllQuantities: true,
  transferQuantity: {
    value: null,
    units: null,
  },
  hasTransferQuantityError: false,
  quantityErrorsAreBlocking: true,
  wellsWithInsufficientQuantity: [],
};

interface TransferPlatesQuantityPaneProps extends TransferPlatesQuantityPaneState {
  cells: Array<AccessibleGridCell>;
  onChangeTransferQuantity: (quantity: LegacyMeasurement) => void;
  onChangeShouldTransferAllQuantities: (e: GenericEvent) => void;
  onChangeTransferQuantityErrorState: (
    hasQuantityError: boolean,
    wellsInViolation: Array<MinimalSourceContainerSummary>,
    quantityErrorsAreBlocking: boolean
  ) => void;
  hasBlockingError: boolean;
}

class TransferPlatesQuantityPane extends React.Component<TransferPlatesQuantityPaneProps> {
  static QUANTITY_OPTIONS = QUANTITY_OPTIONS;

  private validateTransferQuantity(newQuantity: LegacyMeasurement): void {
    const {
      isValid: transferQuantityIsValid,
      sourcesInViolation: wellsWithInsufficientQuantity,
      quantityErrorsAreBlocking,
    } = validateAllTransferQuantities(
      this.props.cells.map(({container}) => ({
        source: container,
        transferQuantity: newQuantity,
      }))
    );
    if (
      !transferQuantityIsValid !== this.props.hasTransferQuantityError ||
      !isEqual(wellsWithInsufficientQuantity, this.props.wellsWithInsufficientQuantity)
    ) {
      this.props.onChangeTransferQuantityErrorState(
        !transferQuantityIsValid,
        wellsWithInsufficientQuantity,
        quantityErrorsAreBlocking
      );
    }
  }

  componentDidUpdate(prevProps: TransferPlatesQuantityPaneProps): void {
    if (!isEqual(prevProps.transferQuantity, this.props.transferQuantity)) {
      this.validateTransferQuantity(this.props.transferQuantity);
    }
  }

  private handleChangeValue = (e: NumericInputChangeEvent): void => {
    this.props.onChangeTransferQuantity({...this.props.transferQuantity, value: e.target.numericValue});
  };

  private handleChangeUnits = ({val}: {val: string | null}): void => {
    this.props.onChangeTransferQuantity({...this.props.transferQuantity, units: val});
  };

  private getUnitTypeFilter = (): ((unitType: CoreUnit['unitType'] & {id: string}) => boolean) | null =>
    getCompatibilityFilterFromSymbols(compact(this.props.cells.map((cell) => cell.container.quantity.units)));

  render(): JSX.Element {
    const shouldShowTransferQuantityError =
      !this.props.hasBlockingError && this.props.hasTransferQuantityError;

    const sharedMeasurementFormProps = {
      measurement: this.props.transferQuantity,
      onChangeUnits: this.handleChangeUnits,
      onChangeValue: this.handleChangeValue,
    };
    return (
      <div>
        <FieldLabel label={`Transfer ${getQuantityDisplayString(false)}`} />
        <div className="u-small-spacing-bottom">
          <label>
            <input
              autoFocus={true}
              type="radio"
              value={QUANTITY_OPTIONS.TRANSFER_ALL}
              checked={this.props.shouldTransferAllQuantities}
              onChange={this.props.onChangeShouldTransferAllQuantities}
            />
            <span className="u-tiny-spacing-left u-spacing-right">
              Transfer all {getQuantityDisplayString(false, true)}
            </span>
          </label>
          <label>
            <input
              type="radio"
              value={QUANTITY_OPTIONS.SPECIFY_QUANTITIES}
              checked={!this.props.shouldTransferAllQuantities}
              onChange={this.props.onChangeShouldTransferAllQuantities}
            />
            <span className="u-tiny-spacing-left">Specify transfer {getQuantityDisplayString(false)}</span>
          </label>
        </div>
        {this.props.shouldTransferAllQuantities ? (
          <div>
            The full {getQuantityDisplayString(false)} will be transferred from each well in the source plate.
          </div>
        ) : (
          <div>
            <div className="u-tiny-spacing-bottom">
              The following {getQuantityDisplayString(false)} will be transferred from all wells in the source
              plate:
            </div>
            <NewMeasurementForm
              {...sharedMeasurementFormProps}
              measurementType={MeasurementType.QUANTITY}
              customUnitTypeFilter={this.getUnitTypeFilter()}
            />
            <div>
              {shouldShowTransferQuantityError && (
                <InsufficientSourceQuantityError
                  invalidWells={this.props.wellsWithInsufficientQuantity}
                  transferQuantity={this.props.transferQuantity}
                  isBlocking={this.props.quantityErrorsAreBlocking}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
export default TransferPlatesQuantityPane;
